import { useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ArrowLeft } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';

export default function BlogPostOverlay({ slug }) {
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);
  const rootRef = useRef(null);

  // Use the pre-click snapshot captured on the Journal card's onClick handler.
  // This avoids drift between click-time and the overlay's mount effect (router
  // navigation, focus-scroll, pending Lenis interpolation, etc).
  useEffect(() => {
    const preClickSnapshot = (typeof window !== 'undefined' && typeof window.__journalSnapshotY === 'number')
      ? window.__journalSnapshotY
      : null;
    const fallbackSnapshot = (window.__lenis && typeof window.__lenis.scroll === 'number')
      ? window.__lenis.scroll
      : (window.scrollY || window.pageYOffset || 0);
    const snapshotY = preClickSnapshot !== null ? preClickSnapshot : fallbackSnapshot;
    // Consume the snapshot so a subsequent direct-URL visit doesn't reuse a stale value
    if (typeof window !== 'undefined') window.__journalSnapshotY = null;

    if (window.__lenis) window.__lenis.stop();
    if (rootRef.current) rootRef.current.scrollTop = 0;

    return () => {
      if (window.__lenis) {
        window.__lenis.start();
        window.__lenis.scrollTo(snapshotY, { immediate: true, force: true });
      } else {
        window.scrollTo(0, snapshotY);
      }
    };
  }, [slug]);

  useLayoutEffect(() => {
    if (!post) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(rootRef.current, { opacity: 0 }, { opacity: 1, duration: 0.45, ease: 'power2.out' });
      gsap.fromTo('.post-hero-img', { clipPath: 'inset(100% 0 0 0)' }, { clipPath: 'inset(0% 0 0 0)', duration: 1.6, ease: 'power2.out', delay: 0.25 });
      gsap.fromTo('.post-title', { opacity: 0, y: 50, filter: 'blur(8px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.4, ease: 'power2.out', delay: 0.4 });
      gsap.fromTo('.post-body p', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1.2, stagger: 0.1, ease: 'power2.out', delay: 0.7 });
    }, rootRef);
    return () => ctx.revert();
  }, [post]);

  const goBack = () => {
    // Since Home is never unmounted, its scroll position is preserved. If the user
    // opened the article from within the site, Home's scroll is > 0. If they landed
    // directly on the article URL, Home is at the top → smooth-scroll to Journal.
    const currentY = (window.__lenis && typeof window.__lenis.scroll === 'number')
      ? window.__lenis.scroll
      : (window.scrollY || window.pageYOffset || 0);
    if (currentY > 100) {
      navigate('/');
    } else {
      navigate('/', { state: { scrollTo: 'journal' } });
    }
  };

  if (!post) {
    return (
      <div
        data-testid="blog-post-page"
        data-lenis-prevent
        className="fixed inset-0 z-[100] bg-[#F5F0E8] overflow-y-auto overscroll-contain flex flex-col items-center justify-center gap-8"
      >
        <p className="font-serif-display text-3xl text-[#2C2C2A]">This story wandered off.</p>
        <button
          data-testid="blog-notfound-back"
          onClick={goBack}
          className="text-xs uppercase tracking-[0.3em] text-[#B85C38] link-underline"
        >
          Return to the Journal
        </button>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      data-testid="blog-post-page"
      data-lenis-prevent
      className="fixed inset-0 z-[100] bg-[#F5F0E8] overflow-y-auto overscroll-contain"
    >
      <div className="sticky top-0 z-10 bg-[#F5F0E8]/85 backdrop-blur-md">
        <div className="flex items-center justify-between px-6 md:px-16 py-5">
          <button
            data-testid="blog-back-button"
            onClick={goBack}
            className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#2C2C2A]/70 hover:text-[#1A4D38] transition-colors duration-500"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <button
            data-testid="blog-post-logo"
            onClick={goBack}
            className="font-serif-display text-lg tracking-[0.18em] uppercase text-[#2C2C2A]"
          >
            Rise <span className="italic text-[#1A4D38]">with</span> Presence
          </button>
        </div>
      </div>

      <article className="pt-16 md:pt-24 pb-32 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#B85C38] mb-6 text-center">
            {post.category} · {post.readTime} · {post.date}
          </p>
          <h1 className="post-title font-serif-display font-light text-4xl sm:text-6xl lg:text-7xl leading-[1.08] text-[#2C2C2A] text-center mb-16 md:mb-20">
            {post.title}
          </h1>
        </div>
        <div className="post-hero-img max-w-5xl mx-auto overflow-hidden mb-16 md:mb-24 will-change-[clip-path]" style={{ clipPath: 'inset(100% 0 0 0)' }}>
          <img src={post.image} alt={post.title} className="w-full aspect-[16/9] object-cover" />
        </div>
        <div className="post-body max-w-2xl mx-auto space-y-10">
          {post.content.map((para, i) => (
            <p
              key={i}
              className={`leading-[2.1] font-light ${i === 0 ? 'font-serif-display text-xl md:text-2xl text-[#1A4D38] italic' : 'text-base md:text-lg text-[#2C2C2A]/80'}`}
            >
              {para}
            </p>
          ))}
        </div>
        <div className="max-w-2xl mx-auto mt-24 pt-12 border-t border-[#2C2C2A]/10 text-center">
          <p className="font-serif-display italic text-xl text-[#2C2C2A]/60 mb-10">
            Take what you need. Leave the rest.
          </p>
          <button
            data-testid="blog-post-return-button"
            onClick={goBack}
            className="px-10 py-4 rounded-full border border-[#1A4D38]/40 text-[#1A4D38] text-xs uppercase tracking-[0.3em] hover:bg-[#1A4D38] hover:text-[#F5F0E8] transition-colors duration-700"
          >
            Return to the Journey
          </button>
        </div>
      </article>
    </div>
  );
}
